import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { InfoTooltip } from '@/components/tours/InfoTooltip';
import { formatPercentage } from '@/lib/utils';
import { useDrillDown, generate30DayForecastDrillDown } from '@/hooks/useDrillDown';
import { ClickableNumber } from '@/components/ui/ClickableNumber';
import { usePermissions } from '@/hooks/usePermissions';
import { DashboardMetrics } from '@/services/metricsService';
import { getForecastTerm } from '@/lib/terminology-config';

interface ThirtyDayForecastMiniCardProps {
  metrics: DashboardMetrics;
}

export default function ThirtyDayForecastMiniCard({ metrics }: ThirtyDayForecastMiniCardProps) {
  const { hasPermission } = usePermissions();
  const { showDrillDown } = useDrillDown();

  if (!hasPermission('view_30day_forecast_mini_card')) {
    return null;
  }

  const currentCash = metrics.currentCash;
  const projectedCash30Days = metrics.projectedCash30Days;
  const expected30DayIncome = metrics.expected30DayIncome;
  const expected30DayExpenses = metrics.expected30DayExpenses;

  const variance = currentCash > 0 ? ((projectedCash30Days - currentCash) / currentCash) * 100 : 0;
  const isPositive = variance >= 0;

  const forecastTerm = getForecastTerm(true);

  return (
    <Card data-tour="forecast" className="h-full" style={{ borderLeft: '3px solid var(--color-info-400)' }}>
      <CardContent className="p-4 flex flex-col justify-between h-full">
        {/* Top row — label + icon badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-base font-medium text-neutral-800 truncate">30-Day {forecastTerm}</span>
            <InfoTooltip
              title={`30-Day ${forecastTerm}`}
              content={`Projected cash position 30 days from now based on outstanding invoices, bills, and scheduled payments. Helps you make short-term decisions.`}
            />
          </div>
          <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-info-100)' }}>
            {isPositive
              ? <TrendingUp className="h-4 w-4" style={{ color: 'var(--color-info-600)' }} />
              : <TrendingDown className="h-4 w-4" style={{ color: 'var(--color-info-600)' }} />
            }
          </div>
        </div>

        {/* Value + sub-text */}
        <div>
          <div className="text-2xl font-bold text-neutral-900 tracking-tight">
            <ClickableNumber
              value={projectedCash30Days}
              onClick={() => showDrillDown('forecast-point', generate30DayForecastDrillDown(projectedCash30Days, currentCash, expected30DayIncome, expected30DayExpenses))}
            />
          </div>
          <div className="flex items-center gap-1 text-xs mt-1">
            <span className="font-semibold text-neutral-800">
              {formatPercentage(variance)}
            </span>
            <span className="font-normal text-neutral-800">vs current</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
